import { useState } from 'react'
import type { AdminDeckDto, CustomCardDto } from '@lunary/api-client'
import { CARDS } from '@lunary/tarot-data'
import { toRoman } from '@lunary/app-core/roman'
import { CardText as CardTextBlock, CardTextPreview, DAILY_TEXT_FIELDS } from '@lunary/ui-web/card-text'
import { CardFace } from '../../shared/ui/card-face'
import { ConfirmDialog } from '../../shared/ui/confirm-dialog'
import { changedFields, usePublishConfirm } from '../../shared/ui/publish-confirm'
import { EMPTY_TEXT, sameText, textOf } from '../../shared/lib/card-text'
import type { CardText } from '../../shared/lib/card-text'
import expert from '../../shared/ui/expert.module.css'
import { deckErrorText } from './errors'
import { useAddCustomCard, useSaveTemplate, useTemplates, useUpdateCustomCard } from './queries'
import styles from './decks.module.css'

export type Selection = { kind: 'major'; cardId: string } | { kind: 'custom'; id: string } | { kind: 'new' }

function MajorForm({ deck, cardId }: { deck: AdminDeckDto; cardId: string }) {
  const card = CARDS.find((c) => c.id === cardId)
  const templates = useTemplates()
  const save = useSaveTemplate()
  const stored = textOf(templates.data?.items.find((t) => t.number === card?.number))
  const { confirmed, dialog } = usePublishConfirm()
  if (!card) return null
  const onSave = async (next: CardText) => {
    await confirmed({
      title: 'Изменить описание для всех колод?',
      text: `Это описание по умолчанию для карты дня. Оно подставится, когда «${card.name}» выберут картой дня в любой колоде.`,
      confirmLabel: 'Опубликовать',
      facts: [['Меняется', changedFields(DAILY_TEXT_FIELDS, stored, next)], ['Не затронет', 'уже выбранные дни, их текст правится отдельно']],
    })
    return save.mutateAsync({ number: card.number, text: next })
  }
  const image = deck.cards.find((c) => c.card_id === cardId)
  return (
    <section className={`${expert.panel} ${styles.cardForm}`} aria-labelledby="card-form-title">
      <div>
        <span className={styles.eyebrow}>Старший аркан {toRoman(card.number)}</span>
        <h2 id="card-form-title">{card.name}</h2>
      </div>
      <b className={styles.formTitle}>Описание по умолчанию для карты дня</b>
      {templates.isPending ? (
        <div className="spinner" aria-label="Загрузка" />
      ) : (
        <CardTextPreview
          fields={DAILY_TEXT_FIELDS}
          value={stored}
          face={<CardFace src={image?.url ?? image?.thumb_url} number={card.number} name={card.name} size="lg" />}
          eyebrow="Карта дня"
          name={card.name}
          saving={save.isPending}
          saved={save.isSuccess}
          error={save.isError || templates.isError ? deckErrorText(save.error ?? templates.error) : null}
          saveLabel="Опубликовать…"
          savedLabel="Опубликовано"
          onSave={onSave}
        />
      )}
      {dialog}
      <p className={styles.hint}>Общее для всех колод. Подставляется, когда эту карту выбирают картой дня; перед публикацией текст можно поправить, шаблон при этом не меняется.</p>
    </section>
  )
}

function CustomForm({ deck, card, onArchived }: { deck: AdminDeckDto; card: CustomCardDto; onArchived: () => void }) {
  const update = useUpdateCustomCard(deck.id)
  const stored = textOf(card.template)
  const [name, setName] = useState(card.name)
  const [draft, setDraft] = useState<CardText>(stored)
  const [confirm, setConfirm] = useState(false)
  const { ask, dialog } = usePublishConfirm()
  const dirty = name.trim() !== card.name || !sameText(draft, stored)
  const onSave = async () => {
    const changed = [name.trim() !== card.name ? 'Название' : '', changedFields(DAILY_TEXT_FIELDS, stored, draft)].filter(Boolean).join(', ')
    const ok = await ask({
      title: 'Сохранить карту?',
      text: `Изменения «${card.name}» подставятся, когда эту карту выберут картой дня.`,
      confirmLabel: 'Опубликовать',
      facts: [['Меняется', changed], ['Не затронет', 'тексты уже выбранных дней']],
    })
    if (ok) update.mutate({ id: card.id, patch: { name: name.trim(), ...draft } })
  }
  return (
    <section className={`${expert.panel} ${styles.cardForm}`} aria-labelledby="card-form-title">
      <div>
        <span className={styles.eyebrow}>Своя карта</span>
        <h2 id="card-form-title">{card.name}</h2>
      </div>
      <div className={styles.preview}>
        <CardFace src={card.image?.url ?? card.image?.thumb_url} number={null} name={card.name} size="lg" />
      </div>
      <label htmlFor="custom-name" className={styles.field}>
        Название
        <input id="custom-name" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
      </label>
      <b className={styles.formTitle}>Описание по умолчанию для карты дня</b>
      <div className={styles.textEdit}>
        <CardTextBlock fields={DAILY_TEXT_FIELDS} value={draft} onChange={setDraft} />
      </div>
      <p className={styles.hint}>Картинку меняют в сетке слева: ↻ на карте или перетащите файл, 2:3, JPEG, PNG или WebP до 8 МБ. У своей карты нет номера аркана: она участвует только в «Карте дня».</p>
      <div className={styles.actions}>
        <button type="button" disabled={!dirty || name.trim().length === 0 || update.isPending} onClick={() => void onSave()}>
          {update.isPending ? 'Сохраняю…' : 'Сохранить'}
        </button>
        <button type="button" className="danger" onClick={() => setConfirm(true)}>
          В архив
        </button>
      </div>
      {update.isError && <p className={styles.error}>{deckErrorText(update.error)}</p>}
      <ConfirmDialog
        open={confirm}
        title="Убрать карту в архив?"
        text={`«${card.name}» пропадёт из выбора карты дня. Уже опубликованные дни с ней останутся как были.`}
        confirmLabel="В архив"
        danger
        pending={update.isPending}
        error={update.isError ? deckErrorText(update.error) : null}
        onClose={() => setConfirm(false)}
        onConfirm={() =>
          update.mutate(
            { id: card.id, patch: { archived: true } },
            {
              onSuccess: () => {
                setConfirm(false)
                onArchived()
              },
            },
          )
        }
      />
      {dialog}
    </section>
  )
}

function NewForm({ deck, onCreated }: { deck: AdminDeckDto; onCreated: (id: string) => void }) {
  const add = useAddCustomCard(deck.id)
  const [name, setName] = useState('')
  const [draft, setDraft] = useState<CardText>(EMPTY_TEXT)
  const trimmed = name.trim()
  return (
    <section className={`${expert.panel} ${styles.cardForm}`} aria-labelledby="card-form-title">
      <div>
        <span className={styles.eyebrow}>Новая карта</span>
        <h2 id="card-form-title">Добавить в колоду</h2>
      </div>
      <label htmlFor="custom-new-name" className={styles.field}>
        Название
        <input id="custom-new-name" value={name} maxLength={60} placeholder="Например, Лунный знак" onChange={(e) => setName(e.target.value)} />
      </label>
      <b className={styles.formTitle}>Описание по умолчанию для карты дня</b>
      <div className={styles.textEdit}>
        <CardTextBlock fields={DAILY_TEXT_FIELDS} value={draft} onChange={setDraft} />
      </div>
      <p className={styles.hint}>Картинку можно загрузить сразу после добавления.</p>
      <div className={styles.actions}>
        <button
          type="button"
          disabled={trimmed.length === 0 || add.isPending}
          onClick={() =>
            add.mutate(
              { name: trimmed, ...draft },
              {
                onSuccess: (next) => {
                  const known = new Set(deck.custom_cards.map((c) => c.id))
                  const created = next.custom_cards.find((c) => !known.has(c.id))
                  if (created) onCreated(created.id)
                },
              },
            )
          }
        >
          {add.isPending ? 'Добавляю…' : 'Добавить'}
        </button>
      </div>
      {add.isError && <p className={styles.error}>{deckErrorText(add.error)}</p>}
    </section>
  )
}

type Props = { deck: AdminDeckDto; selection: Selection; onSelect: (next: Selection | null) => void }

export function CardForm({ deck, selection, onSelect }: Props) {
  if (selection.kind === 'major') return <MajorForm key={selection.cardId} deck={deck} cardId={selection.cardId} />
  if (selection.kind === 'new') return <NewForm deck={deck} onCreated={(id) => onSelect({ kind: 'custom', id })} />
  const card = deck.custom_cards.find((c) => c.id === selection.id)
  if (!card) return null
  return <CustomForm key={card.id} deck={deck} card={card} onArchived={() => onSelect(null)} />
}
